/**
 * ``useRecentErrors`` — TanStack Query hook around
 * ``GET /api/query/producer/{id}/recent-errors``.
 *
 * Issue #259. Powers the Entity Details panel's RECENT ERRORS
 * section. Sourced from the audit-log unsensitive view on the BFF
 * (decode / blob-write / publish failures the ingress path recorded
 * as ``connector.message_quarantined`` audit events) — operator-readable
 * and per-producer scoped per the REQ-AUTH-024 entity-detail
 * visibility split. Mirrors ``useRecentEvents`` in shape.
 *
 * Cross-references:
 *   - Issue #259.
 *   - REQ-UI-049 (RECENT ERRORS section).
 *   - REQ-AUTH-024 (operator role; entity-detail visibility).
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { Role } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";

export type ProducerRecentErrorsResponse = components["schemas"]["ProducerRecentErrorsResponse"];

export const RECENT_ERRORS_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "admin",
  "support_engineer",
];

export const RECENT_ERRORS_STALE_TIME_MS = 30_000;

export function recentErrorsQueryKey(producerId: string, limit: number): readonly unknown[] {
  return ["producer-recent-errors", producerId, limit] as const;
}

export interface UseRecentErrorsOptions {
  producerId: string | null;
  /** Defaults to 10 (max 100). */
  limit?: number;
}

export function useRecentErrors(
  options: UseRecentErrorsOptions,
): UseQueryResult<ProducerRecentErrorsResponse, ApiError> {
  const session = useSession();
  const hasRole = session.hasAnyRole(RECENT_ERRORS_READER_ROLES);
  const limit = options.limit ?? 10;
  const enabled = options.producerId !== null && hasRole;
  return useQuery<ProducerRecentErrorsResponse, ApiError>({
    queryKey:
      options.producerId !== null
        ? recentErrorsQueryKey(options.producerId, limit)
        : ["producer-recent-errors", "disabled"],
    queryFn: async ({ signal }) => {
      if (options.producerId === null) {
        throw new ApiError(0, null, "useRecentErrors: query ran without producerId");
      }
      const url =
        `/api/query/producer/${encodeURIComponent(options.producerId)}` +
        `/recent-errors?limit=${limit}`;
      const raw = await apiFetch<unknown>(url, { signal });
      if (!isResponseShape(raw)) {
        console.error(
          "[meridian/entity-details/use-recent-errors] response failed shape validation",
          raw,
        );
        throw new ApiError(500, raw, "recent-errors response did not match the expected shape");
      }
      return raw;
    },
    enabled,
    staleTime: RECENT_ERRORS_STALE_TIME_MS,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

function isResponseShape(value: unknown): value is ProducerRecentErrorsResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.producer_id === "string" &&
    Array.isArray(obj.items) &&
    typeof obj.truncated === "boolean" &&
    typeof obj.duration_ms === "number"
  );
}
